/** Suggested labels. Stored chambers are not limited to this list. */
export const LEAD_CHAMBERS = [
  'RA',
  'RV',
  'LV',
  'LBB',
  'His',
  'LV epi X 2',
  'LA Epicardial',
  'LV Epicardial',
  'Fractured',
  'RV Septum',
  'Epi RA',
  'RV/SVC',
  'SVC',
  'Atrial',
  'Sub-q',
  'CS',
  'Epicardial',
  'Substernal'
] as const;

export const isActiveLead = (status?: string | null): boolean =>
  (status ?? '').toLowerCase() === 'active';

export const isInactiveLead = (status?: string | null): boolean =>
  (status ?? '').toLowerCase() === 'inactive';

export const isArchivedLead = (status?: string | null): boolean =>
  (status ?? '').toLowerCase() === 'archived';

export const hasLeadData = (lead: {
  implantDate?: string | null;
  leadCapped?: boolean | null;
  leadLocation?: string | null;
  leadModel?: string | null;
  serialNumber?: string | null;
  leadPolarity?: string | null;
  leadManufacturer?: { leadVendorId?: number | null } | null;
}): boolean =>
  Boolean(
    lead.implantDate ||
      (lead.leadCapped !== undefined && lead.leadCapped !== null) ||
      lead.leadLocation ||
      lead.leadModel ||
      lead.serialNumber ||
      lead.leadPolarity ||
      lead.leadManufacturer?.leadVendorId
  );

export const isLeadMissingRequiredChamber = (
  lead: Parameters<typeof hasLeadData>[0] & {
    leadChamber?: string | null;
  }
): boolean => hasLeadData(lead) && !lead.leadChamber?.trim();

export const normalizeLeadChamber = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  const standard = LEAD_CHAMBERS.find((option) => option.toLowerCase() === trimmed.toLowerCase());
  return standard ?? trimmed;
};

export type LeadSavePreparation<T> =
  | { error: 'chamber'; leads: []; missingLead: T }
  | { error: 'status'; leads: [] }
  | { error: null; leads: T[] };

export const prepareLeadsForSave = <
  T extends Parameters<typeof hasLeadData>[0] & {
    id?: string | null;
    leadChamber?: string | null;
    leadStatus?: string | null;
  }
>(
  leads: T[],
  options: { keepChamberOnly?: boolean } = {}
): LeadSavePreparation<T> => {
  // Existing API records with no status are displayed as active. New temp records
  // must still choose a status explicitly, so they remain eligible for validation.
  const statusDefaultedLeads: T[] = leads.map((lead) => ({
    ...lead,
    leadStatus:
      lead.leadStatus || (lead.id && !lead.id.startsWith('tempid-') ? 'Active' : lead.leadStatus)
  })) as T[];

  const editableLeads = statusDefaultedLeads.filter(
    (lead) => !isArchivedLead(lead.leadStatus) && !isInactiveLead(lead.leadStatus)
  );
  const missingChamberLead = editableLeads.find(isLeadMissingRequiredChamber);
  if (missingChamberLead) {
    return { error: 'chamber', leads: [], missingLead: missingChamberLead };
  }

  const prepared = statusDefaultedLeads
    .filter(
      (lead) =>
        isArchivedLead(lead.leadStatus) ||
        (isInactiveLead(lead.leadStatus)
          // Inactive historical rows are part of the replacement set even when their
          // chamber is absent; dropping them would erase implant history.
          ? hasLeadData(lead) || Boolean(lead.leadChamber?.trim())
          : hasLeadData(lead) || (options.keepChamberOnly && Boolean(lead.leadChamber?.trim())))
    )
    .map((lead) =>
      isArchivedLead(lead.leadStatus) || isInactiveLead(lead.leadStatus)
        ? lead
        : normalizeLeadForSave(lead)
    )
    .filter((lead): lead is T => lead !== null)
    .map((lead) => normalizeLeadPolarity(lead));

  if (prepared.some((lead) => !lead.leadStatus)) {
    return { error: 'status', leads: [] };
  }
  return { error: null, leads: prepared };
};

export const normalizeLeadPolarity = <T extends { leadPolarity?: string | null }>(
  lead: T
): T => ({
  ...lead,
  ...(lead.leadPolarity !== undefined && { leadPolarity: lead.leadPolarity || null })
});

export const normalizeLeadForSave = <
  T extends { leadChamber?: string | null; leadPolarity?: string | null }
>(
  lead: T
): (T & { leadChamber: string }) | null => {
  const leadChamber = normalizeLeadChamber(lead.leadChamber);
  if (!leadChamber) {
    return null;
  }
  return normalizeLeadPolarity({ ...lead, leadChamber });
};

/** The chamber stored on the lead, including a value someone typed. */
export const displayLeadChamber = (lead: { leadChamber?: string | null }): string =>
  normalizeLeadChamber(lead.leadChamber);

export const CHAMBER_OTHER = 'Other';
/** Dropdown sentinel. Selecting it opens the new-chamber field instead of storing this string. */
export const CHAMBER_ADD_NEW = '__add_new_chamber__';

export type ChamberOption = { value: string; label: string };

/** Known and stored chambers that match the search, then Other and Add New. */
export function chamberPicklistOptions(input: {
  stored?: Array<string | null | undefined>;
  extra?: Array<string | null | undefined>;
  current?: string | null;
  search?: string;
}): ChamberOption[] {
  const byKey = new Map<string, string>();
  for (const item of [
    input.current ?? '',
    ...LEAD_CHAMBERS,
    ...(input.stored ?? []),
    ...(input.extra ?? [])
  ]) {
    const trimmed = item?.trim() ?? '';
    if (!trimmed || trimmed.toLowerCase() === CHAMBER_OTHER.toLowerCase()) continue;
    const key = trimmed.toLowerCase();
    if (!byKey.has(key)) byKey.set(key, trimmed);
  }
  const needle = input.search?.trim().toLowerCase() ?? '';
  const matches = [...byKey.values()].filter(
    (chamber) => !needle || chamber.toLowerCase().includes(needle)
  );
  return [
    ...matches.map((chamber) => ({ value: chamber, label: chamber })),
    { value: CHAMBER_OTHER, label: CHAMBER_OTHER },
    { value: CHAMBER_ADD_NEW, label: 'Add New' }
  ];
}

/** Labels that share the RA position. The stored chamber text is left as-is. */
const RA_POSITION_LABELS = new Set(['ra', 'atrial', 'atrium', 'right atrial', 'right atrium']);

/**
 * Known chambers in picklist order. RA synonyms sort in the RA slot, above RV.
 * A trailing "Lead" is ignored when matching a known chamber, so "RV Lead" sorts with RV.
 * Anything else sorts alphabetically after the picklist.
 */
export const compareLeadChambers = (
  a: string | null | undefined,
  b: string | null | undefined
): number => {
  const rank = (value: string | null | undefined): [number, number, string] => {
    const trimmed = value?.trim().toLowerCase() ?? '';
    const key = trimmed.replace(/\s+lead$/, '');
    const known = LEAD_CHAMBERS.findIndex((option) => {
      const label = option.toLowerCase();
      return label === trimmed || label === key;
    });
    const picklist = known >= 0 ? known : LEAD_CHAMBERS.length;
    if (RA_POSITION_LABELS.has(key)) return [0, picklist, trimmed];
    if (known >= 0) return [picklist, picklist, trimmed];
    return [LEAD_CHAMBERS.length, LEAD_CHAMBERS.length, trimmed];
  };
  const [rankA, pickA, labelA] = rank(a);
  const [rankB, pickB, labelB] = rank(b);
  if (rankA !== rankB) return rankA - rankB;
  if (pickA !== pickB) return pickA - pickB;
  return labelA.localeCompare(labelB);
};
